import { Pressable, View } from "@-/com.npmjs.react-native";
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  withSpring,
} from "@-/com.npmjs.react-native-reanimated";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { ToolbarGlassSurface } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import type { AuthSheetMode } from "#auth-sheet-context";

const MODE_OPTIONS: readonly { label: string; mode: AuthSheetMode; testID: string }[] = [
  { label: "Log In", mode: "signIn", testID: "auth-sheet-mode-log-in" },
  { label: "Sign Up", mode: "signUp", testID: "auth-sheet-mode-sign-up" },
];

/** Each segment's width (`w-28`); fixed so the selection pill can slide without measuring. */
const SEGMENT_WIDTH = 112;

/**
 * The Log In / Sign Up switch at the top centre of the auth sheet: a liquid-glass capsule whose
 * selection pill slides between the two modes.
 */
export function AuthSheetModeToggle({
  mode,
  onModeChange,
}: {
  mode: AuthSheetMode;
  onModeChange: (mode: AuthSheetMode) => void;
}) {
  const selectedIndex = MODE_OPTIONS.findIndex((option) => option.mode === mode);
  const offset = useDerivedValue(() =>
    withSpring(selectedIndex * SEGMENT_WIDTH, { damping: 24, mass: 0.7, stiffness: 320 }),
  );
  const pillStyle = useAnimatedStyle(() => ({ transform: [{ translateX: offset.get() }] }));

  return (
    <ToolbarGlassSurface
      accessibilityRole="tablist"
      className="flex-row self-center p-1"
      testID="auth-sheet-mode-toggle"
    >
      <View className="absolute inset-y-1 left-1" pointerEvents="none">
        <Animated.View className="h-9 w-28 rounded-full bg-foreground/10" style={pillStyle} />
      </View>
      {MODE_OPTIONS.map((option) => {
        const isSelected = option.mode === mode;
        return (
          <Pressable
            accessibilityLabel={option.label}
            accessibilityRole="tab"
            accessibilityState={{ selected: isSelected }}
            className="h-9 w-28 items-center justify-center rounded-full active:opacity-70"
            key={option.mode}
            onPress={() => {
              if (!isSelected) {
                onModeChange(option.mode);
              }
            }}
            testID={option.testID}
          >
            <Text
              className={
                isSelected
                  ? "text-sm font-semibold text-foreground"
                  : "text-sm font-semibold text-muted-foreground"
              }
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </ToolbarGlassSurface>
  );
}
